import { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Status } from '@intellecteu/common-ui';
import { dayTimeFormatFromUnix, formatBytesDecimal } from 'app/utils';
import { NoEntityData } from 'app/components/core/empty-state';
import { type BackupHistoryDto, errorTransformer } from 'data';
import { TableSkeleton } from 'ui';
import { ErrorWireframe } from '../../error-wireframe';
import { Table } from '../../ui/table/new';

import s from './styles.module.scss';

type BackupsHistoryProps = {
  backupsHistory: BackupHistoryDto[] | undefined;
  historyLoadingError: unknown;
  isFetching: boolean;
};

const mapStatusToColor = {
  succeeded: Status.mode.success,
  running: Status.mode.success,
  pending: Status.mode.info,
  unknown: Status.mode.unknown,
  failed: Status.mode.error,
};

export const BackupsHistory = ({ backupsHistory, historyLoadingError, isFetching }: BackupsHistoryProps) => {
  const cols = useMemo<ColumnDef<BackupHistoryDto>[]>(
    () => [
      {
        header: 'File',
        accessorKey: 'filename',
      },
      {
        header: 'Size',
        accessorKey: 'size',
        cell: (row) => formatBytesDecimal((row.renderValue() as number) || 0),
        size: 25,
        enableSorting: false,
      },
      {
        header: 'Timestamp',
        accessorKey: 'timestamp',
        cell: ({ getValue }) => dayTimeFormatFromUnix(getValue() as string),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }) => {
          const status = getValue() as string;

          return <Status mode={mapStatusToColor[status.toLowerCase()]}>{status}</Status>;
        },
      },
    ],
    [],
  );

  const tableContent =
    backupsHistory && backupsHistory.length > 0 ? (
      <Table data={backupsHistory} columns={cols} wrapperClassName={s.tableWrapperNoPadding} />
    ) : (
      <NoEntityData entityName="backups schedule" />
    );

  if (historyLoadingError) {
    return <ErrorWireframe message={errorTransformer(historyLoadingError)} />;
  }

  return <div>{isFetching ? <TableSkeleton /> : tableContent}</div>;
};
